import React, { useState, useEffect } from "react";
import { ref, onValue, remove, push, update } from "firebase/database";
import { db } from "./firebase";
import { formatCourseId } from "./utils/formatCourse";

const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({
    title: "",
    url: "",
    published: true,
    chapter: "Uncategorized" // Default chapter
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddVideoExpanded, setIsAddVideoExpanded] = useState(false);
  const [expandedVideoId, setExpandedVideoId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc"
  });
  const [chapters, setChapters] = useState(["Uncategorized"]);
  const [isAddingChapter, setIsAddingChapter] = useState(false);
  const [newChapterName, setNewChapterName] = useState("");
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);

  // Fetch courses from Firebase
  useEffect(() => {
    const coursesRef = ref(db, "courses");
    const unsubscribeCourses = onValue(coursesRef, (snapshot) => {
      if (snapshot.exists()) {
        const coursesData = Object.keys(snapshot.val());
        setCourses(coursesData);
      }
    });

    return () => unsubscribeCourses();
  }, [activeCourse]);

  // Fetch videos and chapters for the active course from Firebase
  useEffect(() => {
    if (!activeCourse) return;

    const videosRef = ref(db, `courses/${activeCourse}/videos`);
    const unsubscribeVideos = onValue(videosRef, (snapshot) => {
      if (snapshot.exists()) {
        const videosData = Object.entries(snapshot.val()).map(([id, data]) => ({
          id,
          ...data,
          published: data.published !== false,
          chapter: data.chapter || "Uncategorized",
          title: data.title || "",
          url: data.url || ""
        }));
        setVideos(videosData);

        // Extract unique chapters
        const uniqueChapters = [...new Set(videosData.map(video => video.chapter || "Uncategorized"))];
        setChapters(uniqueChapters);
      } else {
        setVideos([]);
        setChapters(["Uncategorized"]);
      }
    });

    return () => unsubscribeVideos();
  }, [activeCourse]);

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const startEditing = (video) => {
    setEditingId(video.id);
    setEditData({
      title: video.title || "",
      url: video.url || "",
      published: video.published !== false,
      chapter: video.chapter || "Uncategorized"
    });
    setExpandedVideoId(null);
  };

  const saveEdit = () => {
    if (!editingId || !editData.title || !editData.url) {
      alert("Title and URL are required");
      return;
    }

    // Ensure chapter is set and not empty
    const videoData = {
      ...editData,
      published: editData.published !== false,
      chapter: editData.chapter || "Uncategorized",
      title: editData.title.trim(),
      url: editData.url.trim()
    };

    update(ref(db, `courses/${activeCourse}/videos/${editingId}`), videoData)
      .then(() => {
        alert("Video updated successfully");
        setEditingId(null);
      })
      .catch(error => alert("Error updating video: " + error.message));
  };

  const addNewVideo = async () => {
    if (!editData.title || !editData.url) {
      alert("Title and URL are required");
      return;
    }

    // Ensure chapter is set and not empty
    const videoData = {
      ...editData,
      published: editData.published !== false,
      chapter: editData.chapter || "Uncategorized",
      title: editData.title.trim(),
      url: editData.url.trim()
    };

    if (isBulkMode) {
      // Bulk upload to multiple courses
      if (selectedCourses.length === 0) {
        alert("Please select at least one course");
        return;
      }

      try {
        const promises = selectedCourses.map(courseId =>
          push(ref(db, `courses/${courseId}/videos`), videoData)
        );

        await Promise.all(promises);
        alert(`Video added successfully to ${selectedCourses.length} course(s)`);
        setIsAddVideoExpanded(false);
        setSelectedCourses([]);
        // Reset form with the same chapter selected
        setEditData({
          title: "",
          url: "",
          published: true,
          chapter: videoData.chapter // Keep the same chapter selected
        });
      } catch (error) {
        alert("Error adding video to some courses: " + error.message);
      }
    } else {
      // Single course upload
      push(ref(db, `courses/${activeCourse}/videos`), videoData)
        .then(() => {
          alert("Video added successfully");
          setIsAddVideoExpanded(false);
          // Reset form with the same chapter selected
          setEditData({
            title: "",
            url: "",
            published: true,
            chapter: videoData.chapter // Keep the same chapter selected
          });
        })
        .catch(error => alert("Error adding video: " + error.message));
    }
  };

  const handleCourseSelection = (courseId) => {
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };

  const handleSelectAllCourses = () => {
    if (selectedCourses.length === courses.length) {
      setSelectedCourses([]);
    } else {
      setSelectedCourses([...courses]);
    }
  };

  const addNewChapter = () => {
    if (!newChapterName.trim()) {
      alert("Chapter name is required");
      return;
    }

    if (chapters.includes(newChapterName)) {
      alert("Chapter already exists");
      return;
    }

    setChapters([...chapters, newChapterName]);
    setNewChapterName("");
    setIsAddingChapter(false);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      remove(ref(db, `courses/${activeCourse}/videos/${id}`))
        .catch(error => alert("Error deleting video: " + error.message));
    }
  };

  const toggleVideoPreview = (id) => {
    setExpandedVideoId(expandedVideoId === id ? null : id);
  };

  const togglePublishStatus = async (id, currentStatus) => {
    try {
      await update(ref(db, `courses/${activeCourse}/videos/${id}`), {
        published: !currentStatus
      });
    } catch (error) {
      alert("Error updating publish status: " + error.message);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedVideos = React.useMemo(() => {
    let sortableVideos = [...videos];
    if (sortConfig.key) {
      sortableVideos.sort((a, b) => {
        if (sortConfig.key === 'published') {
          const aValue = a.published ? 1 : 0;
          const bValue = b.published ? 1 : 0;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";
        if (sortConfig.direction === "asc") {
          return aValue.toString().localeCompare(bValue.toString());
        } else {
          return bValue.toString().localeCompare(aValue.toString());
        }
      });
    }
    return sortableVideos;
  }, [videos, sortConfig]);

  const filteredVideos = sortedVideos.filter(video =>
    (video.title?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (video.url?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  // Group videos by chapter
  const videosByChapter = filteredVideos.reduce((acc, video) => {
    const chapter = video.chapter || "Uncategorized";
    if (!acc[chapter]) {
      acc[chapter] = [];
    }
    acc[chapter].push(video);
    return acc;
  }, {});

  // Function to convert video URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;

    // Handle YouTube URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId = null;
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1]?.split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('embed/')[1]?.split('?')[0];
      }
      return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0` : null;
    }

    // Handle Vimeo URLs
    if (url.includes('vimeo.com')) {
      let videoId = null;

      // Handle regular vimeo.com URLs
      if (url.includes('vimeo.com/')) {
        const match = url.match(/vimeo\.com\/(\d+)/);
        if (match) {
          videoId = match[1];
        }
      }

      // Handle player.vimeo.com URLs
      if (url.includes('player.vimeo.com')) {
        const match = url.match(/player\.vimeo\.com\/video\/(\d+)/);
        if (match) {
          videoId = match[1];
        }
      }

      // Handle vimeo.com/embed/ URLs
      if (url.includes('vimeo.com/embed/')) {
        const match = url.match(/vimeo\.com\/embed\/(\d+)/);
        if (match) {
          videoId = match[1];
        }
      }

      return videoId ? `https://player.vimeo.com/video/${videoId}?autoplay=0&title=0&byline=0&portrait=0` : null;
    }

    // Handle Google Drive URLs
    if (url.includes('drive.google.com')) {
      if (url.includes('/file/d/')) {
        const fileId = url.split('/file/d/')[1]?.split('/')[0];
        return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : null;
      } else if (url.includes('id=')) {
        const fileId = url.split('id=')[1]?.split('&')[0];
        return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : null;
      }
    }

    // Handle Zoom Recording URLs
    if (url.includes('zoom.us/rec')) {
      // Handle us02web.zoom.us format
      if (url.includes('us02web.zoom.us')) {
        const sharePart = url.split('/rec/share/')[1];
        if (sharePart) {
          const [recordingId, token] = sharePart.split('.');
          if (recordingId && token) {
            return `https://us02web.zoom.us/rec/play/${recordingId}?token=${token}&embedded=true&autoplay=0&mode=play`;
          }
        }
      }
      // Handle regular zoom.us format
      else if (url.includes('/share/')) {
        const sharePart = url.split('/share/')[1];
        if (sharePart) {
          const [recordingId, token] = sharePart.split('?');
          if (recordingId && token) {
            return `https://zoom.us/recording/play/${recordingId}?token=${token}&embedded=true&autoplay=0&mode=play`;
          }
        }
      } else if (url.includes('/play/')) {
        const baseUrl = url.split('?')[0];
        const params = new URLSearchParams(url.split('?')[1] || '');
        params.set('embedded', 'true');
        params.set('autoplay', '0');
        params.set('mode', 'play');
        return `${baseUrl}?${params.toString()}`;
      }
    }

    return null;
  };

  return (
    <div className="p-4 text-gray-900 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-gray-900">Videos</h1>

      {/* Course selector */}
      <div className="mb-6">
        <label htmlFor="videos-course" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
        <select
          id="videos-course"
          value={activeCourse}
          onChange={(e) => {
            setActiveCourse(e.target.value);
            setExpandedVideoId(null);
          }}
          className="p-2 border rounded-md text-gray-900 min-w-[200px]"
        >
          <option value="">Select course</option>
          {courses.map((course) => (
            <option key={course} value={course}>{formatCourseId(course)}</option>
          ))}
        </select>
      </div>

      {!activeCourse ? (
        <div className="flex flex-col items-center justify-center py-16 px-6 bg-gradient-to-b from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-2xl shadow-sm">
          <div className="w-16 h-16 mb-4 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">No course selected</h3>
          <p className="text-gray-500 text-sm max-w-sm">Please select a course from the dropdown above to view and manage content.</p>
        </div>
      ) : (
        <>
      {/* Search and Add Video */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <input
          type="text"
          placeholder="Search videos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="p-2 border rounded-md text-gray-900"
        />
        <div className="md:col-span-2 flex justify-end space-x-2">
          <button
            onClick={() => setIsAddingChapter(!isAddingChapter)}
            className="bg-green-600 text-white p-2 rounded-md hover:bg-green-700"
          >
            {isAddingChapter ? "Cancel" : "Add Chapter"}
          </button>
          <button
            onClick={() => setIsAddVideoExpanded(!isAddVideoExpanded)}
            className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700"
          >
            {isAddVideoExpanded ? "Cancel" : "Add New Video"}
          </button>
        </div>
      </div>

      {/* Add Chapter Form */}
      {isAddingChapter && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">Add New Chapter</h2>
          <div className="flex space-x-2">
            <input
              type="text"
              value={newChapterName}
              onChange={(e) => setNewChapterName(e.target.value)}
              placeholder="Enter chapter name"
              className="flex-1 p-2 border rounded-md text-gray-900"
            />
            <button
              onClick={addNewChapter}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              Add Chapter
            </button>
          </div>
        </div>
      )}

      {/* Add Video Form */}
      {isAddVideoExpanded && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Add New Video</h2>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Single Course</span>
              <button
                onClick={() => {
                  setIsBulkMode(!isBulkMode);
                  if (!isBulkMode) {
                    setSelectedCourses([]);
                  }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isBulkMode ? "bg-blue-600" : "bg-gray-300"
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isBulkMode ? "translate-x-6" : "translate-x-1"
                    }`}
                />
              </button>
              <span className="text-sm text-gray-700">Bulk Upload</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title*</label>
              <input
                type="text"
                name="title"
                value={editData.title}
                onChange={handleEditChange}
                className="w-full p-2 border rounded-md text-gray-900"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Video URL*</label>
              <input
                type="url"
                name="url"
                value={editData.url}
                onChange={handleEditChange}
                className="w-full p-2 border rounded-md text-gray-900"
                required
                placeholder="YouTube or Google Drive URL"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chapter</label>
              <select
                name="chapter"
                value={editData.chapter}
                onChange={handleEditChange}
                className="w-full p-2 border rounded-md text-gray-900"
              >
                {chapters.map(chapter => (
                  <option key={chapter} value={chapter}>{chapter}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="publish-new"
                name="published"
                checked={editData.published}
                onChange={handleEditChange}
                className="h-5 w-5 text-blue-600"
              />
              <label htmlFor="publish-new" className="ml-2 text-sm text-gray-700">
                Publish (visible to students)
              </label>
            </div>
          </div>

          {/* Course Selection for Bulk Upload */}
          {isBulkMode && (
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select Courses ({selectedCourses.length} selected)
                </label>
                <button
                  onClick={handleSelectAllCourses}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  {selectedCourses.length === courses.length ? "Deselect All" : "Select All"}
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                {courses.map(course => (
                  <label
                    key={course}
                    className="flex items-center space-x-2 p-2 hover:bg-gray-200 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(course)}
                      onChange={() => handleCourseSelection(course)}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{formatCourseId(course)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Single Course Display */}
          {!isBulkMode && (
            <div className="mt-4 p-3 bg-blue-50 rounded-md">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Course:</span> {formatCourseId(activeCourse)}
              </p>
            </div>
          )}

          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={() => {
                setIsAddVideoExpanded(false);
                setIsBulkMode(false);
                setSelectedCourses([]);
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={addNewVideo}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              {isBulkMode ? `Add to ${selectedCourses.length} Course(s)` : "Add Video"}
            </button>
          </div>
        </div>
      )}

      {/* Videos by Chapter */}
      {Object.entries(videosByChapter).map(([chapter, chapterVideos]) => (
        <div key={chapter} className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-900">{chapter}</h2>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-300 text-black">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("title")}
                    >
                      Title {sortConfig.key === "title" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      URL
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("published")}
                    >
                      Status {sortConfig.key === "published" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chapterVideos.map(video => (
                    <React.Fragment key={video.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleVideoPreview(video.id)}>
                        {editingId === video.id ? (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="text"
                                name="title"
                                value={editData.title}
                                onChange={handleEditChange}
                                className="p-1 border rounded w-full text-gray-900"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="url"
                                name="url"
                                value={editData.url}
                                onChange={handleEditChange}
                                className="p-1 border rounded w-full text-gray-900"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                name="published"
                                checked={editData.published}
                                onChange={handleEditChange}
                                className="h-5 w-5 text-blue-600"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap space-x-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm"
                              >
                                Save
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingId(null); }}
                                className="bg-gray-500 text-white px-3 py-1 rounded text-sm"
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                              {video.title}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap truncate max-w-xs text-gray-900">
                              <a
                                href={video.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {video.url}
                              </a>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={video.published !== false}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  togglePublishStatus(video.id, video.published !== false);
                                }}
                                className="h-5 w-5 text-blue-600"
                              />
                              <span className="ml-2 text-gray-700">
                                {video.published !== false ? "Published" : "Draft"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap space-x-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); startEditing(video); }}
                                className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDelete(video.id); }}
                                className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                              >
                                Delete
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleVideoPreview(video.id); }}
                                className="bg-purple-600 text-white px-3 py-1 rounded text-sm"
                              >
                                {expandedVideoId === video.id ? "Hide" : "Preview"}
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                      {expandedVideoId === video.id && (
                        <tr>
                          <td colSpan="4" className="px-6 py-4 bg-gray-50">
                            <div className="relative w-full pt-[56.25%] bg-black rounded-lg overflow-hidden">
                              <iframe
                                src={getEmbedUrl(video.url)}
                                className="absolute top-0 left-0 w-full h-full"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                                allowFullScreen
                                frameBorder="0"
                                style={{ border: 'none' }}
                                title={video.title}
                                referrerPolicy="origin"
                                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-presentation"
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}

      {filteredVideos.length === 0 && (
        <div className="p-4 text-center text-gray-500">
          No videos found matching your criteria
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AdminVideos;